import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { vodsApi } from '../services/api';
import { VOD } from '../shared';
import { 
  Video, 
  Download, 
  Upload as UploadIcon, 
  Trash2, 
  Eye, 
  Calendar,
  HardDrive,
  Filter,
  Search,
  X,
  MessageSquare,
  RefreshCw
} from 'lucide-react';
import { fixThumbnailUrl } from '../utils/thumbnails';
import ConfirmDialog from '../components/ConfirmDialog';
import ProgressBar from '../components/ProgressBar';
import { useMultipleVODProgress } from '../hooks/useVODProgress';

export default function VODs() {
  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVOD, setSelectedVOD] = useState<VOD | null>(null);
  const [vodToDelete, setVodToDelete] = useState<VOD | null>(null);
  const queryClient = useQueryClient();

  const { data: vodsData, isLoading } = useQuery(
    ['vods', page, sourceFilter],
    () => vodsApi.getVODs(page, 20, sourceFilter || undefined).then(res => res.data.data),
    {
      keepPreviousData: true,
    }
  );

  // Get progress for all processing VODs
  const vodProgressData = useMultipleVODProgress(
    vodsData?.vods?.map(vod => ({ id: vod.id, status: vod.status })) || []
  );

  const deleteMutation = useMutation(vodsApi.deleteVOD, {
    onSuccess: () => {
      queryClient.invalidateQueries('vods');
      queryClient.invalidateQueries('dashboard-stats');
      queryClient.invalidateQueries('dashboard-activity');
      setSelectedVOD(null);
      setVodToDelete(null);
    },
  });

  const handleDeleteClick = (vod: VOD) => {
    setVodToDelete(vod);
  };

  const handleDeleteConfirm = async () => {
    if (vodToDelete) {
      await deleteMutation.mutateAsync(vodToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setVodToDelete(null);
  };

  const filteredVODs = vodsData?.vods.filter(vod =>
    vod.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vod.filename.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4 animate-pulse"></div>
        <div className="grid gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
              <div className="flex space-x-4">
                <div className="w-32 h-20 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My VODs</h1>
          <p className="mt-1 text-sm text-gray-500">
            {vodsData?.pagination.total || 0} videos in your archive
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search VODs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="input w-auto"
              >
                <option value="">All Sources</option>
                <option value="manual">Manual Upload</option>
                <option value="twitch">Twitch Backup</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* VODs Grid */}
      {filteredVODs.length > 0 ? (
        <div className="space-y-4">
          {filteredVODs.map((vod) => (
            <div key={vod.id} className="card hover:shadow-md transition-shadow">
              <div className="flex space-x-4">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                    {vod.thumbnailUrl ? (
                      <img
                        src={fixThumbnailUrl(vod.thumbnailUrl, 320, 180) || ''}
                        alt={vod.title}
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <Video className={`h-8 w-8 text-gray-400 ${vod.thumbnailUrl ? 'hidden' : ''}`} />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {vod.title}
                      </h3>
                      {vod.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {vod.description}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {vod.source === 'manual' && (
                        <button
                          onClick={() => {/* TODO: Implement replace upload */}}
                          className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                          title="Replace with high-quality upload"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedVOD(vod)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(vod)}
                        disabled={deleteMutation.isLoading}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                        title="Delete VOD"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      {vod.source === 'twitch' ? (
                        <Download className="h-4 w-4 mr-1 text-twitch-600" />
                      ) : (
                        <UploadIcon className="h-4 w-4 mr-1 text-blue-600" />
                      )}
                      <span className="capitalize">{vod.source}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <HardDrive className="h-4 w-4 mr-1" />
                      <span>{formatFileSize(vod.fileSize)}</span>
                    </div>

                    <div className="flex items-center">
                      <MessageSquare className="h-4 w-4 mr-1 text-gray-400" />
                      <span className="text-sm">
                        {vod.chatBackupSize ? `Chat: ${formatFileSize(vod.chatBackupSize)}` : 'No chat backup'}
                      </span>
                      {vod.source === 'manual' && !vod.chatBackupSize && (
                        <button className="ml-2 text-xs text-blue-600 hover:text-blue-800">
                          Add chat
                        </button>
                      )}
                    </div>
                    
                    {vod.duration && (
                      <div className="flex items-center">
                        <span>{formatDuration(vod.duration)}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(vod.uploadedAt)}</span>
                    </div>
                  </div>

                  {/* Status and Tags */}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        vod.status === 'ready' ? 'bg-green-100 text-green-800' :
                        vod.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        vod.status === 'error' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {vod.status}
                      </span>
                      
                      {vod.isLongTerm && (
                        <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                          Long-term
                        </span>
                      )}
                      
                      {vod.tags.map((tag) => (
                        <span key={tag} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Progress Bar for Processing VODs */}
                  {vod.status === 'processing' && vodProgressData.data?.[vod.id] && (
                    <ProgressBar
                      vodId={vod.id}
                      status={vod.status}
                      progressInfo={vodProgressData.data[vod.id].progress}
                      className="mt-4"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Video className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchQuery || sourceFilter ? 'No VODs match your filters' : 'No VODs found'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery || sourceFilter 
              ? 'Try adjusting your search or filters'
              : 'Start by uploading a VOD or syncing from Twitch'
            }
          </p>
        </div>
      )}

      {/* Pagination */}
      {vodsData && vodsData.pagination.total > 20 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, vodsData.pagination.total)} of{' '}
            {vodsData.pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={!vodsData.pagination.hasMore}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* VOD Details Modal */}
      {selectedVOD && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">{selectedVOD.title}</h2>
                <button
                  onClick={() => setSelectedVOD(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {selectedVOD.thumbnailUrl && (
                <img
                  src={fixThumbnailUrl(selectedVOD.thumbnailUrl, 640, 360) || ''}
                  alt={selectedVOD.title}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}

              {selectedVOD.description && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600">{selectedVOD.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-900">Source:</span>
                  <span className="ml-2 capitalize">{selectedVOD.source}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">File Size:</span>
                  <span className="ml-2">{formatFileSize(selectedVOD.fileSize)}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Duration:</span>
                  <span className="ml-2">{formatDuration(selectedVOD.duration)}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Status:</span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    selectedVOD.status === 'ready' ? 'bg-green-100 text-green-800' :
                    selectedVOD.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                    selectedVOD.status === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedVOD.status}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Uploaded:</span>
                  <span className="ml-2">{formatDate(selectedVOD.uploadedAt)}</span>
                </div>
                {selectedVOD.streamedAt && (
                  <div>
                    <span className="font-medium text-gray-900">Streamed:</span>
                    <span className="ml-2">{formatDate(selectedVOD.streamedAt)}</span>
                  </div>
                )}
              </div>

              {selectedVOD.tags.length > 0 && (
                <div className="mt-4">
                  <span className="font-medium text-gray-900">Tags:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedVOD.tags.map((tag) => (
                      <span key={tag} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => handleDeleteClick(selectedVOD)}
                  disabled={deleteMutation.isLoading}
                  className="btn-danger"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete VOD
                </button>
                <button
                  onClick={() => setSelectedVOD(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!vodToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete VOD"
        message={vodToDelete ? `Are you sure you want to delete "${vodToDelete.title}"? This action cannot be undone and will permanently remove the VOD and all associated data.` : ''}
        confirmText="Delete VOD"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
}